/**
 * Procedural sound generator using Web Audio API.
 * Generates retro arcade-style sound effects without external audio files.
 * Each sound function creates a short audio buffer and plays it through
 * Phaser's sound manager for proper volume integration.
 */

import type Phaser from 'phaser';

const SAMPLE_RATE = 44100;

function getAudioContext(scene: Phaser.Scene): AudioContext | null {
  const soundManager = scene.sound as unknown as { context?: AudioContext };
  return soundManager.context ?? null;
}

function createAudioBuffer(
  ctx: AudioContext,
  samples: Float32Array,
): AudioBuffer {
  const buffer = ctx.createBuffer(1, samples.length, SAMPLE_RATE);
  buffer.copyToChannel(samples as unknown as Float32Array<ArrayBuffer>, 0);
  return buffer;
}

function generateTone(
  frequency: number,
  duration: number,
  options?: { readonly decay?: number; readonly wave?: 'sine' | 'square' | 'sawtooth' },
): Float32Array {
  const decay = options?.decay ?? 0.5;
  const wave = options?.wave ?? 'square';
  const length = Math.floor(SAMPLE_RATE * duration);
  const samples = new Float32Array(length);

  for (let i = 0; i < length; i++) {
    const t = i / SAMPLE_RATE;
    const envelope = Math.exp(-t * decay / duration);
    let value: number;

    switch (wave) {
      case 'sine':
        value = Math.sin(2 * Math.PI * frequency * t);
        break;
      case 'sawtooth':
        value = 2 * ((frequency * t) % 1) - 1;
        break;
      case 'square':
      default:
        value = Math.sin(2 * Math.PI * frequency * t) > 0 ? 1 : -1;
        break;
    }

    samples[i] = value * envelope * 0.3;
  }

  return samples;
}

function concatSamples(arrays: readonly Float32Array[]): Float32Array {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Float32Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}

export function playMenuNavigate(scene: Phaser.Scene): void {
  const samples = generateTone(440, 0.06, { decay: 2, wave: 'square' });
  playGeneratedSound(scene, samples);
}

export function playMenuSelect(scene: Phaser.Scene): void {
  const part1 = generateTone(523, 0.08, { decay: 1.5, wave: 'square' });
  const part2 = generateTone(784, 0.12, { decay: 1.5, wave: 'square' });
  const samples = concatSamples([part1, part2]);
  playGeneratedSound(scene, samples);
}

export function playVolumeChange(scene: Phaser.Scene): void {
  const samples = generateTone(330, 0.04, { decay: 3, wave: 'sine' });
  playGeneratedSound(scene, samples);
}

export function playPowerPillSound(scene: Phaser.Scene): void {
  // Rising two-tone with shimmer effect - signals power activation
  const part1 = generateTone(330, 0.08, { decay: 1, wave: 'square' });
  const part2 = generateTone(523, 0.08, { decay: 1, wave: 'square' });
  const part3 = generateTone(659, 0.1, { decay: 0.8, wave: 'sine' });
  const part4 = generateTone(784, 0.15, { decay: 0.5, wave: 'sine' });
  const samples = concatSamples([part1, part2, part3, part4]);
  playGeneratedSound(scene, samples);
}

export function playDeathSound(scene: Phaser.Scene): void {
  // Descending tone sequence - classic arcade death jingle
  const part1 = generateTone(600, 0.12, { decay: 1, wave: 'square' });
  const part2 = generateTone(500, 0.12, { decay: 1, wave: 'square' });
  const part3 = generateTone(400, 0.12, { decay: 1, wave: 'square' });
  const part4 = generateTone(300, 0.15, { decay: 1, wave: 'square' });
  const part5 = generateTone(200, 0.2, { decay: 0.5, wave: 'sine' });
  const samples = concatSamples([part1, part2, part3, part4, part5]);
  playGeneratedSound(scene, samples);
}

export function playExtraLifeSound(scene: Phaser.Scene): void {
  // Bright ascending chime - celebratory tone for earning a bonus life
  const part1 = generateTone(523, 0.08, { decay: 1, wave: 'sine' });
  const part2 = generateTone(659, 0.08, { decay: 1, wave: 'sine' });
  const part3 = generateTone(784, 0.08, { decay: 1, wave: 'sine' });
  const part4 = generateTone(1047, 0.15, { decay: 0.3, wave: 'sine' });
  const part5 = generateTone(1047, 0.12, { decay: 0.4, wave: 'square' });
  const samples = concatSamples([part1, part2, part3, part4, part5]);
  playGeneratedSound(scene, samples);
}

export function playLevelCompleteSound(scene: Phaser.Scene): void {
  // Triumphant fanfare - ascending sequence with a strong finish
  const part1 = generateTone(392, 0.12, { decay: 0.8, wave: 'square' });
  const part2 = generateTone(523, 0.12, { decay: 0.8, wave: 'square' });
  const part3 = generateTone(659, 0.12, { decay: 0.8, wave: 'square' });
  const part4 = generateTone(784, 0.15, { decay: 0.6, wave: 'square' });
  const part5 = generateTone(1047, 0.25, { decay: 0.3, wave: 'sine' });
  const part6 = generateTone(784, 0.1, { decay: 0.5, wave: 'sine' });
  const part7 = generateTone(1047, 0.35, { decay: 0.2, wave: 'sine' });
  const samples = concatSamples([part1, part2, part3, part4, part5, part6, part7]);
  playGeneratedSound(scene, samples);
}

export function playFruitPickupSound(scene: Phaser.Scene): void {
  // Quick rising blip - satisfying pickup effect
  const part1 = generateTone(440, 0.05, { decay: 2, wave: 'sine' });
  const part2 = generateTone(660, 0.05, { decay: 2, wave: 'sine' });
  const part3 = generateTone(880, 0.1, { decay: 1, wave: 'sine' });
  const samples = concatSamples([part1, part2, part3]);
  playGeneratedSound(scene, samples);
}

export function playGhostEatSound(scene: Phaser.Scene): void {
  // Rising swoosh with a satisfying crunch - arcade ghost-eating effect
  const part1 = generateTone(200, 0.06, { decay: 2, wave: 'square' });
  const part2 = generateTone(400, 0.06, { decay: 1.5, wave: 'square' });
  const part3 = generateTone(600, 0.06, { decay: 1, wave: 'sine' });
  const part4 = generateTone(900, 0.1, { decay: 0.5, wave: 'sine' });
  const samples = concatSamples([part1, part2, part3, part4]);
  playGeneratedSound(scene, samples);
}

/** Tracks waka-waka alternation state (false=low pitch, true=high pitch) */
let wakaToggle = false;

export function playWakaWakaSound(scene: Phaser.Scene): void {
  // Classic alternating two-tone chomp: low pitch on one dot, high pitch on next
  const freq = wakaToggle ? 520 : 440;
  wakaToggle = !wakaToggle;
  const samples = generateTone(freq, 0.06, { decay: 4, wave: 'square' });
  playGeneratedSound(scene, samples);
}

export function playGameOverSound(scene: Phaser.Scene): void {
  // Dramatic descending sequence for game over
  const part1 = generateTone(440, 0.25, { decay: 0.8, wave: 'square' });
  const part2 = generateTone(370, 0.25, { decay: 0.8, wave: 'square' });
  const part3 = generateTone(330, 0.25, { decay: 0.8, wave: 'square' });
  const part4 = generateTone(262, 0.5, { decay: 0.3, wave: 'sine' });
  const samples = concatSamples([part1, part2, part3, part4]);
  playGeneratedSound(scene, samples);
}

function playGeneratedSound(
  scene: Phaser.Scene,
  samples: Float32Array,
): void {
  try {
    const ctx = getAudioContext(scene);
    if (!ctx || ctx.state === 'closed') {
      return;
    }

    const buffer = createAudioBuffer(ctx, samples);
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const gainNode = ctx.createGain();
    gainNode.gain.value = scene.sound.volume;

    source.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Clean up audio nodes after playback to prevent memory leaks
    source.onended = () => {
      source.disconnect();
      gainNode.disconnect();
    };

    source.start(0);
  } catch {
    // Silently ignore audio errors (browser restrictions, etc.)
  }
}
